/**
 * OpenClaw SDK compatibility layer — dynamic import wrappers with caching.
 *
 * Each wrapper resolves a capability by trying an ordered list of
 * (specifier, symbol) candidates and caching the first that yields a function.
 * If none match, the wrapper returns a no-op result.
 *
 * WHY A LIST AND NOT ONE PATH. Until 2026-08-05 each wrapper imported a single
 * `openclaw/dist/plugin-sdk/...` path. OpenClaw 2026.7.1's `exports` map does
 * not expose `./dist/*`, so all three threw ERR_PACKAGE_PATH_NOT_EXPORTED and
 * cached `false` — every one of these capabilities was silently dead on the
 * version our own production agent runs. Verified against the installed 2026.7.1.
 *
 * The comment this file used to carry — "degrades gracefully on older OpenClaw
 * versions" — was the trap. The break came from a NEWER harness, and graceful
 * degradation made a real capability loss look identical to a harness that
 * never had the API. Same failure shape as #480 Lever 2's version detection.
 *
 * ORDER: newest specifier first, legacy `dist/` path last. Two miss modes must
 * both fall through — the module failing to import, AND the module importing
 * cleanly without the symbol (2026.7.1 renamed `requestHeartbeatNow` to
 * `requestHeartbeat`, so the module is present but the old name is not).
 */

import { createRequire } from "node:module";
import { join as joinPath } from "node:path";
import { pathToFileURL } from "node:url";

/** One place a capability might live, in one harness generation. */
export interface SdkCandidate {
  specifier: string;
  symbol: string;
}

/** Injectable so resolution is testable — a hardcoded `import()` is not. */
export type SdkImporter = (specifier: string) => Promise<Record<string, any>>;

const defaultImporter: SdkImporter = (specifier) => import(/* @vite-ignore */ specifier);

/**
 * #1481 — an importer that resolves specifiers as the OpenClaw package at
 * `root` exports them (package self-reference), not from wherever this file
 * happens to be. Doctor runs from npx's cache, where `openclaw/...` does not
 * resolve, so its probe reported every capability dead on every harness.
 */
export function importerFrom(root: string): SdkImporter {
  const req = createRequire(joinPath(root, "package.json"));
  return (specifier) => import(/* @vite-ignore */ pathToFileURL(req.resolve(specifier)).href);
}

/**
 * Walk the candidates in order and return the first function found.
 * Returns false when nothing matches. Never throws.
 */
export async function resolveSdkFn(
  candidates: SdkCandidate[],
  importer: SdkImporter = defaultImporter,
): Promise<((...args: any[]) => any) | false> {
  for (const { specifier, symbol } of candidates) {
    try {
      const mod = await importer(specifier);
      const found = mod?.[symbol] ?? mod?.default?.[symbol];
      // Must be callable. A same-named non-function export is a miss, not a hit.
      if (typeof found === "function") return found;
    } catch {
      // Blocked, absent, or a different install layout — try the next.
    }
  }
  return false;
}

/* ── Capability tables ──────────────────────────────────────────────────── */

/** Wake the agent proactively before an outbound send. */
export const HEARTBEAT_CANDIDATES: SdkCandidate[] = [
  // 2026.7.x — exported subpath, and the symbol lost its `Now` suffix.
  { specifier: "openclaw/plugin-sdk/heartbeat-runtime", symbol: "requestHeartbeat" },
  // <=2026.6.x — deep dist path, original name.
  { specifier: "openclaw/dist/plugin-sdk/infra/heartbeat-wake.js", symbol: "requestHeartbeatNow" },
];

/* ── Wrappers ───────────────────────────────────────────────────────────── */

let _heartbeatFn: ((opts?: { reason?: string }) => Promise<void>) | false | null = null;

/**
 * Request an immediate heartbeat wake from the OpenClaw gateway.
 * Returns true if the API was available and called, false otherwise. Never throws.
 */
export async function requestHeartbeatNow(opts?: { reason?: string }): Promise<boolean> {
  if (_heartbeatFn === null) {
    _heartbeatFn = (await resolveSdkFn(HEARTBEAT_CANDIDATES)) as
      | ((opts?: { reason?: string }) => Promise<void>)
      | false;
  }
  if (typeof _heartbeatFn === "function") {
    try {
      await _heartbeatFn(opts);
      return true;
    } catch {
      return false;
    }
  }
  return false;
}
